// client/src/intro-exit/VariableQuiz.jsx

/**
 * A single comprehension question, shown after the variable introduction and
 * before the practice round: which of the borrower's raw features is the most
 * informative about default?
 *
 * It exists because the previous batch produced a contradiction. Experts told the
 * exit survey they had leaned on their private credit score, yet they got half the
 * cases the credit score decides wrong -- so the instruction page was conveying
 * "this one is yours" without conveying "this one is worth the most". A ranking a
 * participant has to commit to, and be corrected on, is a stronger test of that
 * than a page they can scroll past.
 *
 * The correct answer depends on which private feature the participant holds: for an
 * expert the credit score dominates both public features, while for a non-expert
 * revolving utilization is the weakest of the three and debt-to-income is the most
 * informative thing they can see. The AI Risk Recommendation is deliberately outside
 * the question -- this is a manipulation check on the raw features, and it has to
 * work the same way for an assisted expert as for an unassisted one, since the point
 * is that the credit score is a strong signal whether or not the AI is also present.
 * Which is why the stem names that exclusion rather than leaving the participant to
 * infer it from an options list.
 *
 * The stage timer releases everyone when it expires; a correct answer is the only
 * other way out. There is no skip control here and none in Stage.jsx, and that is
 * load-bearing -- a participant who could click past this would be recorded
 * identically to one who never answered.
 *
 * Layout, type and control styling are copied from the verification quiz in
 * GameIntroAndQuiz.jsx so the two quizzes read as the same screen. The one
 * deliberate departure is the answer text, which keeps the public/private colour
 * coding used everywhere else in the study rather than plain ink.
 */

import React, { useState } from "react";
import { variable_theme } from "../theme.js";

export default function VariableQuiz({ player, onBack, onPass }) {
  const isExpert = player.get("isExpert");
  const hasAI = player.get("hasAI");
  const [choice, setChoice] = useState("");
  const [quizError, setQuizError] = useState(false);

  const tPublic = variable_theme.public;
  const tPrivate = variable_theme.private;

  // Options are the two public features plus whichever private feature this
  // participant was given -- never the one they cannot see.
  const privateLabel = isExpert ? "Credit Score" : "Revolving Utilization";
  const options = [
    { val: "dti", text: "Debt-to-Income", color: tPublic.textColor },
    { val: "limit", text: "Total Credit Limit", color: tPublic.textColor },
    { val: "private", text: privateLabel, color: tPrivate.textColor },
  ];
  const correct = isExpert ? "private" : "dti";

  // Answers are written to the player, not just held in local state, so they reach
  // the export -- the same idiom as `quizFailedAttempts` in GameIntroAndQuiz.jsx.
  //
  // Recording the CHOICE and not merely pass/fail is what makes the stage timer
  // survivable as a measure. Previously the only trace was the stage's `submit`
  // flag, which collapses three different participants into one: someone who picked
  // the wrong option, someone who read too slowly to pick anything, and someone who
  // ignored the screen. With the choice stored, a recorded wrong answer means they
  // answered and were wrong; no recorded answer means they never got that far.
  //
  // The FIRST choice is the comprehension measure. After the error message the
  // participant is eliminating options rather than recalling the instruction page,
  // so a later correct answer is a different quantity from an initial one.
  const handleValidationSubmit = (e) => {
    e.preventDefault();
    if (player.get("variableQuizFirstChoice") === undefined) {
      player.set("variableQuizFirstChoice", choice);
    }
    player.set("variableQuizLastChoice", choice);

    if (choice === correct) {
      player.set("variableQuizPassed", true);
      setQuizError(false);
      onPass();
    } else {
      player.set("variableQuizAttempts", (player.get("variableQuizAttempts") || 0) + 1);
      setQuizError(true);
    }
  };

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="border-b pb-3">
        <h2 className="app-section-title text-gray-800">One Quick Check</h2>
        <p className="app-section-subtitle text-gray-500 mt-0.5">
          Answer correctly to start the practice round.
        </p>
      </div>

      {quizError && (
        <div className="app-error p-4 bg-rose-50 border border-rose-200 text-rose-700 rounded-xl animate-pulse">
          ✕ Verification Failed: That is not the most informative variable. Please review the
          instructions and try again.
        </div>
      )}

      <form onSubmit={handleValidationSubmit} className="app-body space-y-6 text-gray-700">
        <div className="space-y-2">
          {/* The stem must not count the variables on the screen. The instruction page
              tells an AI participant they will be shown FOUR variables and gives the AI
              Risk Recommendation its own bullet, table row and plot; asking about "the 3
              variables you can see" then misdescribes their screen, and leaves the one
              they may consider most informative absent from the options with no reason
              given. In the batch that first ran this question, both AI cells passed at
              roughly half the rate of the corresponding no-AI cells, which is as
              consistent with that mismatch as with anything about comprehension.
              Naming the exclusion outright keeps the question about the raw features,
              which is what it was always meant to measure. */}
          <label className="app-question block text-gray-800">
            {hasAI
              ? "Setting the AI Risk Recommendation aside, which of the borrower's own variables tells you the most about whether they will default?"
              : "Which of the borrower's variables tells you the most about whether they will default?"}
          </label>
          <div className="flex flex-col gap-2">
            {options.map((opt) => (
              <label
                key={opt.val}
                className={`app-choice p-3 border rounded-xl flex items-center gap-3 cursor-pointer transition-colors ${
                  choice === opt.val
                    ? "bg-blue-50 border-blue-400 font-medium text-blue-900"
                    : "bg-gray-50 border-gray-200 hover:bg-gray-100/70"
                }`}
              >
                <input
                  type="radio"
                  name="most_informative"
                  value={opt.val}
                  checked={choice === opt.val}
                  onChange={(e) => {
                    setChoice(e.target.value);
                    setQuizError(false);
                  }}
                  className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                />
                <span className={`font-bold ${opt.color}`}>{opt.text}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Action Form Footer Layout */}
        <div className="pt-6 border-t flex justify-between items-center">
          <button
            type="button"
            onClick={onBack}
            className="app-btn px-5 py-2 border border-gray-300 rounded-xl hover:bg-gray-50 transition"
          >
            Back to Instructions
          </button>
          <button
            type="submit"
            className="app-btn px-10 py-2.5 bg-black text-white rounded-xl hover:bg-emerald-500 transition shadow-md"
          >
            Submit &amp; Verify
          </button>
        </div>
      </form>
    </div>
  );
}
